import { useState, useEffect, useCallback } from 'react';
import { 
    JobDetailBuilderTimelineResult,
    BuilderActivityRecord
} from '../providers/types';
import AxiosInstance from '../utils/axiosInstance';

interface UseBuilderTimelineResult {
    data: JobDetailBuilderTimelineResult | null;
    isLoading: boolean;
    error: string | null;
    refetch: () => void;
}

// API returns the same structure, no transformation needed
// Just ensure dates are parsed if needed
const transformActivityRecord = (
    record: BuilderActivityRecord
): BuilderActivityRecord => ({
    ...record,
    // Convert ISO date string to Date object if your components need it
    // Otherwise, keep as string and parse in display components
    activityDate: record.activityDate // Keep as string for now
});

const transformApiResponse = (
    apiData: JobDetailBuilderTimelineResult
): JobDetailBuilderTimelineResult => {
    return {
        jobId: apiData.jobId,
        stage5Activities: apiData.stage5Activities.map(transformActivityRecord),
        closingActivities: apiData.closingActivities.map(transformActivityRecord)
    };
};

export const useBuilderTimeline = (
    jobId: number | null
): UseBuilderTimelineResult => {
    const [data, setData] = useState<JobDetailBuilderTimelineResult | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchTimeline = useCallback(async () => {
        if (!jobId) {
            setData(null);
            setIsLoading(false);
            setError(null);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await AxiosInstance.get<JobDetailBuilderTimelineResult>(
                `/api/cba/job-details/builder-timeline/${jobId}`
            );
            console.log('API Response:', response.data); // Debug log

            const result = response.data;

            if (!result || typeof result !== 'object') {
                throw new Error('Invalid response format');
            }

            // Validate that required arrays exist
            if (!Array.isArray(result.stage5Activities)) {
                console.error('Missing stage5Activities array:', result);
                throw new Error('API response missing stage5Activities array');
            }

            if (!Array.isArray(result.closingActivities)) {
                console.error('Missing closingActivities array:', result);
                throw new Error('API response missing closingActivities array');
            }

            const transformedData = transformApiResponse(result);
            setData(transformedData);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
            setError(errorMessage);
            console.error('Error fetching builder timeline:', err);
            setData(null);
        } finally {
            setIsLoading(false);
        }
    }, [jobId]);

    useEffect(() => {
        fetchTimeline();
    }, [fetchTimeline]);

    return {
        data,
        isLoading,
        error,
        refetch: fetchTimeline
    };
};